import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import InstallAppBanner from '../components/ui/InstallAppBanner';
import HeroCarousel from '../components/ui/HeroCarousel';
import {
  Sun, Cloud, MessageSquare, BookOpen,
  TrendingUp, Leaf, Check, ArrowRight, Download, ShieldCheck
} from 'lucide-react';

// D2.3: build-status labels removed; cards keep the 8 real features but no "Fully Functional" / "Light automation" copy.
const features = [
  { icon: Sun, title: "Daily Overview", desc: "See what needs attention today at a glance" },
  { icon: Cloud, title: "Weather Alerts", desc: "Freeze, heat, and wind notifications with reminders" },
  { icon: MessageSquare, title: "Farm Chat", desc: "Ask questions about your farm anytime" },
  { icon: BookOpen, title: "Farm Notebook", desc: "Keep organized notes by date and category" },
  { icon: TrendingUp, title: "Market Prices", desc: "Track regional pricing and trends" },
  { icon: Leaf, title: "Resource Tracking", desc: "Log water, soil, and input usage" },
];

// D3.2: five real testimonials collected with permission. Verbatim, first name + last initial only.
const testimonials = [
  {
    quote: "I've tried keeping all this stuff in notebooks and spreadsheets, and Fencepost is just easier. I can actually keep up with what's happening around the farm without digging through a bunch of notes.",
    name: "Timothy K."
  },
  {
    quote: "I downloaded Fencepost for the garden stuff, but I've ended up using way more of it than I expected. It feels like it was made by somebody who actually has chores to do, not somebody sitting in an office guessing what farmers need.",
    name: "Blake R."
  },
  {
    quote: "I really like having my planting dates and what I've got growing all in one place. I'm usually terrible about keeping records, but this makes it simple enough that I actually do it.",
    name: "Tina C."
  },
  {
    quote: "Been messing around with Fencepost for a bit now and I'm impressed. There's a lot packed into it without making it a pain to figure out.",
    name: "Mike N."
  },
  {
    quote: "I have a garden, chickens, and about a hundred things I'm trying to remember at any given time. Fencepost gives me one place to keep track of it all, which is exactly what I needed.",
    name: "Sara T."
  }
];

// D4.2: pricing card feature list reconciled with the features section and the brief.
const pricingFeatures = [
  "Livestock tracking (goats, sheep, cattle)",
  "Poultry and flock management",
  "Garden planner and harvest logs",
  "Milk and egg production tracking",
  "Breeding and birth records",
  "Vet records and health history",
  "Grant-ready PDF reports (USDA / NRCS aligned)",
  "CSV data exports",
  "Market insights (projected vs actual)",
  "Daily weather alerts",
  "Farm notebook and photo gallery",
];

const Landing = () => {
  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Navigation — D2.4 / D4.1 / D5.3: Pricing, Features, About links added. */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/fencepost-logo.png?v=6" alt="Fencepost" className="w-8 h-8 rounded-lg" />
            <span className="font-heading font-bold text-xl text-foreground">Fencepost</span>
          </Link>
          <div className="hidden md:flex items-center gap-5 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-features-link">Features</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-pricing-link">Pricing</a>
            <a href="#about" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-about-link">About</a>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link to="/login">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground px-2 sm:px-4 text-sm sm:text-base" data-testid="nav-login-btn">
                Log in
              </Button>
            </Link>
            <Link to="/register">
              <Button className="btn-primary px-2.5 sm:px-4 text-sm sm:text-base" data-testid="nav-signup-btn">
                <span className="sm:hidden">Start Trial</span>
                <span className="hidden sm:inline">Start Free Trial</span>
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero — D1.5 / D2.1: real app screenshot replaces the lifestyle photo. D1.2 / D1.6: subhead names the fragmentation pain in the audience's own words. */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <Leaf className="w-4 h-4" />
                Built for small farms &amp; homesteads
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Keeping daily<br />
                <span className="text-primary">farm life organized.</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg">
                Replace the notebooks, spreadsheets, and five apps tracking your farm with one daily overview.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="btn-primary w-full sm:w-auto text-base px-8 py-4" data-testid="hero-start-trial-btn">
                    Start 7-Day Free Trial
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Check className="w-5 h-5 text-primary" />
                  <span>Then $9.99/month</span>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-up stagger-2">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid — D1.6: section intro names the fragmentation pain. D2.3: build-status labels removed. */}
      <section id="features" className="py-16 md:py-24 px-4 bg-muted/30 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything Your Farm Needs
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Instead of flipping between notebooks, spreadsheets, and single-purpose apps, Fencepost gives you one place for every part of your farm — synthesized into a daily overview you can actually act on.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`card-farm p-6 animate-fade-in stagger-${(index % 5) + 1}`}
              >
                <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center bg-primary/20">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* D3.2 — Real testimonials, placed between the feature grid and the pricing section. */}
      <section id="testimonials" className="py-16 md:py-24 px-4 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              What early users are saying
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Verbatim from people using Fencepost on their own farms, shared with permission.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <figure
                key={t.name}
                className={`card-farm p-6 flex flex-col gap-4 animate-fade-in stagger-${(i % 5) + 1}`}
                data-testid={`testimonial-${i}`}
              >
                <blockquote className="text-foreground text-base leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="text-sm text-muted-foreground font-medium mt-auto">
                  — {t.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* D3.3 — Founder identity block replaces the anonymous "not the fantasy" quote. */}
      <section id="about" className="py-16 md:py-24 px-4 bg-muted/30 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-[auto_1fr] gap-8 items-center">
            <div className="mx-auto md:mx-0">
              <img
                src="/founder-ally.jpg"
                alt="Ally Kelley, founder of Fencepost, in Tombstone, Arizona"
                className="w-40 h-40 md:w-48 md:h-48 rounded-full object-cover shadow-lg border-4 border-background"
                loading="lazy"
              />
            </div>
            <div className="text-center md:text-left space-y-4">
              <p className="text-xs uppercase tracking-wider text-primary font-semibold">
                Who built this
              </p>
              <blockquote className="font-heading text-xl md:text-2xl text-foreground leading-snug">
                &ldquo;I built Fencepost out of necessity for my own small farm in Tombstone, Arizona. I was having a hard time keeping track of everything I needed to across the whole operation, and nothing out there did it the way I needed it to. So I built it.&rdquo;
              </blockquote>
              <p className="text-muted-foreground">
                — Ally, founder of Fencepost
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing — D4.2: expanded feature list. D3.4 / D1.4: 30-day money-back guarantee + data ownership line under the CTA. */}
      <section id="pricing" className="py-16 md:py-24 px-4 bg-primary/5 scroll-mt-20">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Simple, Honest Pricing
          </h2>
          <div className="card-farm p-8 mb-8">
            <div className="flex items-baseline justify-center gap-2 mb-4">
              <span className="font-heading text-5xl font-bold text-foreground">$9.99</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <p className="text-muted-foreground mb-6">
              Start with a 7-day free trial. Cancel anytime.
            </p>
            <ul className="space-y-3 text-left max-w-md mx-auto mb-8">
              {pricingFeatures.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/register">
              <Button className="btn-primary w-full text-base py-4" data-testid="pricing-start-trial-btn">
                Start Your Free Trial
              </Button>
            </Link>
            <div className="mt-5 space-y-2 text-left max-w-md mx-auto">
              <div className="flex items-start gap-2 text-sm text-foreground">
                <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <span>30-day money-back guarantee on monthly plans, no questions asked.</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-foreground">
                <Download className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <span>Your records are yours &mdash; export everything as CSV anytime.</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            No credit card required for trial. Powered by Stripe for secure billing.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border/60">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/fencepost-logo.png?v=6" alt="Fencepost" className="w-6 h-6 rounded-md" />
            <span className="font-heading font-semibold text-foreground">Fencepost</span>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap justify-center">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">
              Features
            </a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">
              Pricing
            </a>
            <a href="#about" className="text-muted-foreground hover:text-foreground transition-colors">
              About
            </a>
            <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
              Privacy
            </a>
            <a href="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
              Terms
            </a>
          </div>
          <p className="text-sm text-muted-foreground text-center md:text-right">
            © 2026 Fencepost &middot; Built by Ally in Tombstone, AZ
          </p>
        </div>
      </footer>

      <InstallAppBanner />
    </div>
  );
};

export default Landing;
